// Mapeamento das opções dos comboboxes de diversidade para os IDs das
// reference tables em `core/backend/seeds/reference-tables/{gender,ethnicity,
// sexual_orientation,family_income,is_pwd,pwd}.json`. Trocar IDs nesses
// seeds quebra o mapeamento.

export type PwdOptionKey =
  | 'no'
  | 'rather-not-inform'
  | 'hearing'
  | 'physical'
  | 'intellectual'
  | 'visual'
  | 'multiple'
  | 'autism'
  | 'multiple-disabilities';

export interface PwdOption {
  key: PwdOptionKey;
  label: string;
  isPwdId: 1 | 2 | 3;
  pwdId: number | null;
}

// Disability types follow the Figma "Diversidade" spec order + labels. The
// `pwdId` maps to `core.pwd` (eureca-core seed `reference-tables/pwd.json`);
// `multiple` (10) and `multiple-disabilities` (11) were added there.
export const PWD_OPTIONS: PwdOption[] = [
  { key: 'no', label: 'Não', isPwdId: 2, pwdId: null },
  { key: 'rather-not-inform', label: 'Prefiro não informar', isPwdId: 3, pwdId: null },
  { key: 'hearing', label: 'Deficiência Auditiva', isPwdId: 1, pwdId: 1 },
  { key: 'physical', label: 'Deficiência Física', isPwdId: 1, pwdId: 2 },
  { key: 'intellectual', label: 'Deficiência Intelectual ou Cognitiva', isPwdId: 1, pwdId: 3 },
  { key: 'visual', label: 'Deficiência Visual', isPwdId: 1, pwdId: 6 },
  { key: 'multiple', label: 'Deficiência Múltipla', isPwdId: 1, pwdId: 10 },
  { key: 'autism', label: 'Transtorno do Espectro Autista (TEA)', isPwdId: 1, pwdId: 5 },
  { key: 'multiple-disabilities', label: 'Múltiplas deficiências', isPwdId: 1, pwdId: 11 },
];

export function pwdOptionFromIds(
  isPwdId: number | null,
  pwdId: number | null
): PwdOptionKey | null {
  if (isPwdId == null) return null;
  if (isPwdId === 2) return 'no';
  if (isPwdId === 3) return 'rather-not-inform';
  return PWD_OPTIONS.find((o) => o.pwdId === pwdId)?.key ?? null;
}

export function isPwdTypeRequiringAttestation(key: PwdOptionKey | ''): boolean {
  return key !== '' && key !== 'no' && key !== 'rather-not-inform';
}

interface ReferenceOption<K extends string> {
  key: K;
  id: number;
  label: string;
}

export type GenderKey =
  | 'agender'
  | 'gender-fluid'
  | 'cis-male'
  | 'trans-male'
  | 'cis-female'
  | 'trans-female'
  | 'non-binary'
  | 'rather-not-inform'
  | 'other';

export const GENDER_OPTIONS: ReferenceOption<GenderKey>[] = [
  { key: 'agender', id: 1, label: 'Pessoa agênero' },
  { key: 'gender-fluid', id: 2, label: 'Pessoa de gênero fluido' },
  { key: 'cis-male', id: 3, label: 'Homem cisgênero' },
  { key: 'trans-male', id: 4, label: 'Homem transgênero' },
  { key: 'cis-female', id: 5, label: 'Mulher cisgênero' },
  { key: 'trans-female', id: 6, label: 'Mulher transgênero' },
  { key: 'non-binary', id: 7, label: 'Pessoa não-binária' },
  { key: 'rather-not-inform', id: 8, label: 'Prefiro não informar' },
  { key: 'other', id: 9, label: 'Outro' },
];

export type EthnicityKey =
  | 'yellow'
  | 'white'
  | 'indigenous'
  | 'brown'
  | 'black'
  | 'rather-not-inform';

export const ETHNICITY_OPTIONS: ReferenceOption<EthnicityKey>[] = [
  { key: 'yellow', id: 1, label: 'Pessoa amarela' },
  { key: 'white', id: 2, label: 'Pessoa branca' },
  { key: 'indigenous', id: 3, label: 'Pessoa indígena' },
  { key: 'brown', id: 4, label: 'Pessoa parda' },
  { key: 'black', id: 5, label: 'Pessoa preta' },
  { key: 'rather-not-inform', id: 6, label: 'Prefiro não informar' },
];

export type SexualOrientationKey =
  | 'asexual'
  | 'bisexual'
  | 'heterosexual'
  | 'homosexual'
  | 'pansexual'
  | 'other'
  | 'rather-not-inform';

export const SEXUAL_ORIENTATION_OPTIONS: ReferenceOption<SexualOrientationKey>[] = [
  { key: 'asexual', id: 1, label: 'Assexual' },
  { key: 'bisexual', id: 2, label: 'Bissexual' },
  { key: 'heterosexual', id: 3, label: 'Heterossexual' },
  { key: 'homosexual', id: 4, label: 'Homossexual' },
  { key: 'pansexual', id: 5, label: 'Pansexual' },
  { key: 'other', id: 6, label: 'Outro' },
  { key: 'rather-not-inform', id: 7, label: 'Prefiro não informar' },
];

export type FamilyIncomeKey =
  | 'below_minimum_wage'
  | 'one_to_two_minimum_wages'
  | 'three_to_five_minimum_wages'
  | 'above_five_minimum_wages'
  | 'rather-not-inform';

export const FAMILY_INCOME_OPTIONS: ReferenceOption<FamilyIncomeKey>[] = [
  { key: 'below_minimum_wage', id: 1, label: 'Abaixo de um salário mínimo' },
  { key: 'one_to_two_minimum_wages', id: 2, label: 'De 1 a 2 salários mínimos' },
  { key: 'three_to_five_minimum_wages', id: 3, label: 'De 3 a 5 salários mínimos' },
  { key: 'above_five_minimum_wages', id: 4, label: 'Acima de 5 salários mínimos' },
  { key: 'rather-not-inform', id: 5, label: 'Prefiro não informar' },
];

function findById<K extends string>(options: ReferenceOption<K>[], id: number | null): K | null {
  if (id == null) return null;
  return options.find((o) => o.id === id)?.key ?? null;
}

function findIdByKey<K extends string>(options: ReferenceOption<K>[], key: K | ''): number | null {
  if (key === '') return null;
  return options.find((o) => o.key === key)?.id ?? null;
}

export function genderKeyFromId(id: number | null): GenderKey | null {
  return findById(GENDER_OPTIONS, id);
}
export function genderIdFromKey(key: GenderKey | ''): number | null {
  return findIdByKey(GENDER_OPTIONS, key);
}

export function ethnicityKeyFromId(id: number | null): EthnicityKey | null {
  return findById(ETHNICITY_OPTIONS, id);
}
export function ethnicityIdFromKey(key: EthnicityKey | ''): number | null {
  return findIdByKey(ETHNICITY_OPTIONS, key);
}

export function sexualOrientationKeyFromId(id: number | null): SexualOrientationKey | null {
  return findById(SEXUAL_ORIENTATION_OPTIONS, id);
}
export function sexualOrientationIdFromKey(key: SexualOrientationKey | ''): number | null {
  return findIdByKey(SEXUAL_ORIENTATION_OPTIONS, key);
}

export function familyIncomeKeyFromId(id: number | null): FamilyIncomeKey | null {
  return findById(FAMILY_INCOME_OPTIONS, id);
}
export function familyIncomeIdFromKey(key: FamilyIncomeKey | ''): number | null {
  return findIdByKey(FAMILY_INCOME_OPTIONS, key);
}
